import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal, { ModalDirection } from 'components/modal/Modal';
import { actions } from 'actions';
import { connect } from 'unistore/preact';
import i18n from 'micro-i18n';
import { IconClose } from 'components/icons/Icons';
import { JotType, KeyboardEventKeyCode, QuestionType } from 'consts';
import classNames from 'classnames';
import FeedbackQuestion from 'components/questions/FeedbackQuestion';
import {
  getDeckState,
  handleNoShiftKeyCodeTriggers,
  handleShiftKeyCodeTriggers,
  isNullOrUndefOrEmpty,
  onFocusRef,
  toObject
} from 'utils';

//FIXME: SOF-617 requires the following imports to be moved to components/widget-components.scss
// due to lazy loading of the widget inside an iframe. Keeping this here for potential fix down the road.
// import 'components/report/ReportModal.scss';

const ReportModal = ({
  questionsData = {},
  questionIndex,
  widgetState,
  id,
  showModal,
  onClose,
  theme,
  jot,
  getResults,
  closeFocusRef,
  focusable = false
}) => {
  if (isNullOrUndefOrEmpty(id) || isNullOrUndefOrEmpty(onClose) || isNullOrUndefOrEmpty(closeFocusRef)) {
    return null;
  }
  const { currentQuestion } = getDeckState({
    widgetState, questionsData, questionIndex
  });

  const { id: currQuestionId } = toObject(currentQuestion);

  const modalFocusRef = useRef(null);
  const closeButtonBottomRef = useRef(null);

  const reportOptions = [
    { id: 0, text: i18n.t('REPORT_FEEDBACK_ANSWER_0') },
    { id: 1, text: i18n.t('REPORT_FEEDBACK_ANSWER_1') },
    { id: 2, text: i18n.t('REPORT_FEEDBACK_ANSWER_2') },
    { id: 3, text: i18n.t('REPORT_FEEDBACK_ANSWER_3') },
    { id: 4, text: i18n.t('REPORT_FEEDBACK_ANSWER_4') },
  ];

  const reportQuestion = {
    id: 0,
    type: QuestionType.Radio,
    text: i18n.t('REPORT_FEEDBACK_TITLE'),
    options: reportOptions
  };

  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState(null);

  const handleSubmit = () => {
    jot({ space: 'poll', type: JotType.ReportSubmit, question: currentQuestion, data: { ro: selectedOptionId } });
    // If a user likes a question, don't skip it.
    (selectedOptionId !== 0) && getResults({questionsData, question: currentQuestion, currQuestionId, skip: true});
    setReportSubmitted(true);
  };

  const onTabKey = (event, focusRef, checkShift = false) => {
    const shiftKeyHandler = checkShift ? handleShiftKeyCodeTriggers : handleNoShiftKeyCodeTriggers;

    return shiftKeyHandler(event, [KeyboardEventKeyCode.Tab], () => onFocusRef(focusRef));
  };

  const resetFeedbackModal = (close = false) => {
    setSelectedOptionId(null);
    setReportSubmitted(false);
    close && onClose();
  };

  const onCloseReportModal = (isReportSubmitted) => {
    // Only jot cancel if user hasn't submitted reason for report.
    !isReportSubmitted && jot({ space: 'poll', type: JotType.ReportCancel, question: currentQuestion });
    resetFeedbackModal(true);
  };

  return (
    <Modal id={id}
           className="csw-report-modal"
           showModal={showModal}
           direction={ModalDirection.Right}
           onClose={onClose}
           theme={theme}
           openFocusRef={modalFocusRef}
           closeFocusRef={closeFocusRef}
           focusable={focusable}
           ariaLabeledById={`${id}-heading`}>
      <header ref={modalFocusRef} tabIndex="-1"
              onKeyDown={(e) =>
                onTabKey(e, closeButtonBottomRef, true)}>
        <span role="heading" aria-level="1"
              id={`${id}-heading`}>{i18n.t('REPORT_TITLE')}</span>
        <button type="button" className="csw-menu-btn csw-btn csw-btn-icon outline"
                onClick={() => onCloseReportModal(reportSubmitted)}>
          <IconClose/>
          <span className="sr-only">{i18n.t('REPORT_CLOSE')}</span>
        </button>

      </header>
      <div className="csw-modal-scroll">
        {!reportSubmitted &&
          <>
            <div className="csw-report-modal-title-text outline" id="report-question"
                 role="heading"
                 aria-level="1"
                 title={reportQuestion.text}
                 tabIndex="0">
              <span>{reportQuestion.text}</span>
            </div>
            <FeedbackQuestion
              labelId="report-question"
              question={reportQuestion}
              setSelectedOptionId={setSelectedOptionId}
            />
          </>}
        {reportSubmitted &&
          <div className="csw-report-feedback">
            <div className="csw-report-feedback-thanks">{i18n.t('REPORT_FEEDBACK_THANKS')}</div>

            <div className="csw-btns csw-btns-center">
              <button
                type="button"
                role="button"
                className="csw-btn csw-btn-primary"
                onClick={onCloseReportModal}>
                {i18n.t('MENU_CLOSE')}
              </button>
            </div>
          </div>}
      </div>

      <footer className="csw-report-modal-footer csw-btns csw-btns-center">
        {!reportSubmitted &&
          <button
            type="button"
            role="button"
            disabled={isNullOrUndefOrEmpty(selectedOptionId)}
            className={classNames('csw-btn csw-btn-primary csw-btn-submit outline')}
            tabIndex="0"
            onClick={handleSubmit}>
            {i18n.t('REPORT_SUBMIT_BUTTON')}
          </button>}

        <button type="button"
                className="csw-menu-btn csw-modal-footer-btn csw-btn csw-btn-icon outline sr-only sr-only-focusable"
                ref={closeButtonBottomRef}
                onKeyDown={(e) => onTabKey(e, modalFocusRef)}
                onClick={onCloseReportModal}
                aria-label={i18n.t('MENU_CLOSE')}
                title={i18n.t('MENU_CLOSE')}>
          <IconClose/>
          <span className="sr-only">{i18n.t('MENU_CLOSE')}</span>
        </button>
      </footer>
    </Modal>
  );
};

ReportModal.propTypes = {
  id: PropTypes.string.isRequired,
  widgetState: PropTypes.number,
  questionsData: PropTypes.object,
  questionIndex: PropTypes.number,
  showModal: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  theme: PropTypes.string,
  jot: PropTypes.func,
  getResults: PropTypes.func,
  closeFocusRef: PropTypes.object.isRequired,
  focusable: PropTypes.bool
};

export default connect('widgetState,questionsData,questionIndex', actions)(ReportModal);
